/**
 * iter733 — Extracted from SalesPage.jsx. The full "Add New Sale"
 * form covering branch/online tabs, branch selection chips, customer
 * picker (online only), date, payment-line editor with running
 * Cash/Bank/Credit/Online totals, platform picker (when online > 0),
 * discount, and notes. All data-testid attributes preserved verbatim.
 *
 * Props:
 *   - formData, setFormData
 *   - activeTab, setActiveTab
 *   - branches, customers, platforms
 *   - totals — { cash, bank, credit, online, subtotal, discount, total }
 *   - onSubmit, onCancel
 *   - addPaymentRow, removePaymentRow, updatePaymentRow
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, Truck } from 'lucide-react';
import { cur } from '@/lib/currency';
import BranchTaxBadge from '@/components/BranchTaxBadge';

export function SaleFormCard({
  formData,
  setFormData,
  activeTab,
  setActiveTab,
  branches,
  customers,
  platforms,
  totals,
  onSubmit,
  onCancel,
  addPaymentRow,
  removePaymentRow,
  updatePaymentRow,
}) {
  return (
    <Card className="border-border" data-testid="sale-form-card">
      <CardHeader>
        <CardTitle className="font-outfit">Add New Sale</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs
          value={activeTab}
          onValueChange={(val) => {
            setActiveTab(val);
            if (val === 'online') {
              setFormData({ ...formData, sale_type: val, payment_details: [{ mode: 'online_platform', amount: '' }] });
            } else {
              setFormData({ ...formData, sale_type: val, payment_details: [{ mode: 'cash', amount: '' }] });
            }
          }}
        >
          <TabsList className="mb-4">
            <TabsTrigger value="branch" data-testid="branch-sale-tab-2">Branch Sale</TabsTrigger>
            <TabsTrigger value="online" data-testid="online-sale-tab-2" className="bg-purple-100 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <Truck size={14} className="mr-1" /> Online Sale
            </TabsTrigger>
          </TabsList>

          <form onSubmit={onSubmit}>
            <div className="space-y-6">
              <TabsContent value="online" className="mt-0 space-y-4">
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-700">
                  <Label className="text-purple-700 dark:text-purple-300 mb-3 block text-sm font-medium">
                    <Truck size={16} className="inline mr-2" />
                    Select Delivery Platform *
                  </Label>
                  <div className="flex gap-2 flex-wrap">
                    {platforms.filter(p => p.is_active !== false).map((platform, i) => {
                      const colors = ['bg-error/20 border-error/30 text-error', 'bg-success/20 border-success/30 text-success', 'bg-info/20 border-info/30 text-info', 'bg-warning/20 border-warning/30 text-warning', 'bg-pink-100 border-pink-300 text-pink-700'];
                      return (
                        <button
                          key={platform.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, platform_id: platform.id })}
                          className={`px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all ${colors[i % colors.length]} ${formData.platform_id === platform.id ? 'ring-2 ring-purple-500 ring-offset-1 scale-105 shadow-md' : 'opacity-80 hover:opacity-100 hover:scale-105'}`}
                        >
                          {platform.name}
                          {platform.commission_rate > 0 && <span className="text-xs ml-1 opacity-70">({platform.commission_rate}%)</span>}
                        </button>
                      );
                    })}
                  </div>
                  {platforms.length === 0 && (
                    <p className="text-sm text-purple-600 mt-2">
                      No platforms configured. <a href="/platforms" className="underline">Add platforms</a> first.
                    </p>
                  )}
                </div>
              </TabsContent>

              <div>
                <Label>Branch *</Label>
                <div className="flex gap-2 flex-wrap mt-2">
                  {branches.map((branch, i) => {
                    const colors = ['bg-orange-100 border-orange-300 text-orange-700', 'bg-success/20 border-success/30 text-success', 'bg-info/20 border-info/30 text-info', 'bg-purple-100 border-purple-300 text-purple-700', 'bg-cyan-100 border-cyan-300 text-cyan-700'];
                    return (
                      <button
                        key={branch.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, branch_id: branch.id })}
                        className={`px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all ${colors[i % colors.length]} ${formData.branch_id === branch.id ? 'ring-2 ring-primary ring-offset-1 scale-105 shadow-md' : 'opacity-80 hover:opacity-100 hover:scale-105'}`}
                      >
                        {branch.name}
                      </button>
                    );
                  })}
                </div>
                {formData.branch_id && (
                  <div className="mt-2">
                    <BranchTaxBadge branchId={formData.branch_id} compact />
                  </div>
                )}
              </div>

              <TabsContent value="online" className="mt-0">
                <div>
                  <Label>Customer (Optional - for credit)</Label>
                  <div className="flex gap-2 flex-wrap mt-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, customer_id: '' })}
                      className={`px-3 py-1.5 rounded-xl border-2 text-xs font-medium transition-all bg-muted border-border text-foreground ${!formData.customer_id ? 'ring-2 ring-primary ring-offset-1' : 'opacity-80 hover:opacity-100'}`}
                    >
                      Walk-in
                    </button>
                    {customers.map((customer, i) => {
                      const colors = ['bg-warning/20 border-warning/30 text-warning', 'bg-teal-100 border-teal-300 text-teal-700', 'bg-error/20 border-error/30 text-error', 'bg-indigo-100 border-indigo-300 text-indigo-700', 'bg-lime-100 border-lime-300 text-lime-700'];
                      return (
                        <button
                          key={customer.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, customer_id: customer.id })}
                          className={`px-3 py-1.5 rounded-xl border-2 text-xs font-medium transition-all ${colors[i % colors.length]} ${formData.customer_id === customer.id ? 'ring-2 ring-primary ring-offset-1 scale-105 shadow-md' : 'opacity-80 hover:opacity-100 hover:scale-105'}`}
                        >
                          {customer.name}{customer.ref ? ` (${customer.ref})` : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </TabsContent>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    data-testid="date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <Label>Payment Details *</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addPaymentRow} className="rounded-full">
                    <Plus size={14} className="mr-1" />
                    Add Payment
                  </Button>
                </div>
                <div className="space-y-3 border rounded-lg p-4 bg-secondary/30">
                  {formData.payment_details.map((payment, index) => (
                    <div key={index} className="flex gap-3 items-end">
                      <div className="flex-1">
                        <Label className="text-xs">Mode</Label>
                        <Select value={payment.mode} onValueChange={(val) => updatePaymentRow(index, 'mode', val)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cash">Cash</SelectItem>
                            <SelectItem value="bank">Bank</SelectItem>
                            <SelectItem value="credit">Credit (Customer)</SelectItem>
                            <SelectItem value="online_platform">Online Platform</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs">Amount</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={payment.amount}
                          onChange={(e) => updatePaymentRow(index, 'amount', e.target.value)}
                          placeholder="0.00"
                        />
                      </div>
                      {formData.payment_details.length > 1 && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removePaymentRow(index)}
                          className="text-error"
                        >
                          <X size={16} />
                        </Button>
                      )}
                    </div>
                  ))}

                  <div className="pt-3 border-t space-y-2">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div className="p-2 bg-cash/10 rounded border border-cash/30">
                        <div className="text-xs text-muted-foreground">Cash</div>
                        <div className="font-bold text-cash"> {cur()} {totals.cash.toFixed(2)}</div>
                      </div>
                      <div className="p-2 bg-bank/10 rounded border border-bank/30">
                        <div className="text-xs text-muted-foreground">Bank</div>
                        <div className="font-bold text-bank"> {cur()} {totals.bank.toFixed(2)}</div>
                      </div>
                      <div className="p-2 bg-credit/10 rounded border border-credit/30">
                        <div className="text-xs text-muted-foreground">Credit</div>
                        <div className="font-bold text-credit"> {cur()} {totals.credit.toFixed(2)}</div>
                      </div>
                      <div className="p-2 bg-purple-500/10 rounded border border-purple-500/30">
                        <div className="text-xs text-muted-foreground flex items-center gap-1"><Truck size={12} />Online</div>
                        <div className="font-bold text-purple-600"> {cur()} {totals.online.toFixed(2)}</div>
                      </div>
                    </div>

                    {totals.online > 0 && (
                      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
                        <Label className="text-sm text-purple-700 dark:text-purple-300 mb-2 block">
                          <Truck size={14} className="inline mr-1" />
                          Select Delivery Platform *
                        </Label>
                        <Select
                          value={formData.platform_id}
                          onValueChange={(val) => setFormData({ ...formData, platform_id: val })}
                        >
                          <SelectTrigger className="bg-white dark:bg-foreground">
                            <SelectValue placeholder="Choose platform..." />
                          </SelectTrigger>
                          <SelectContent>
                            {platforms.filter(p => p.is_active !== false).map(platform => (
                              <SelectItem key={platform.id} value={platform.id}>
                                {platform.name} {platform.commission_rate > 0 && `(${platform.commission_rate}% commission)`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {platforms.length === 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            No platforms configured. Go to Settings → Platforms to add.
                          </p>
                        )}
                      </div>
                    )}

                    <div className="space-y-2 pt-2 border-t">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal:</span>
                        <span className="font-medium"> {cur()} {totals.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">Discount:</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.discount}
                          onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                          placeholder="0.00"
                          className="h-8 max-w-[120px]"
                          data-testid="discount-input"
                        />
                      </div>
                      <div className="flex justify-between text-lg font-bold pt-2 border-t">
                        <span>Final Amount:</span>
                        <span className="text-primary"> {cur()} {totals.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  data-testid="notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button type="submit" data-testid="submit-sale-button" className="rounded-full">Add Sale</Button>
              <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">
                Cancel
              </Button>
            </div>
          </form>
        </Tabs>
      </CardContent>
    </Card>
  );
}
